import { useQuery } from "@tanstack/react-query";
import { 
  getBooks, 
  getValueChart, 
  getPerformanceResult, 
  getTWRChart,
  getDividendsReceived,
  getGlobalDividendsReceived,
  getBenchmarkChart,
  getCurrentValuation,
  getRecordCount,
} from "../services/portfolio-service";

export const portfolioKeys = {
  all: ["portfolio"] as const,
  uiPrefs: {
    portfolioPositionsTable: () =>
      [...portfolioKeys.all, "uiPrefs", "portfolioPositionsTable"] as const,
  },
  books: () => [...portfolioKeys.all, "books"] as const,
  valueChart: (bookId: string, period: string) => [...portfolioKeys.all, "valueChart", bookId, period] as const,
  performance: (bookId: string, period: string) => [...portfolioKeys.all, "performance", bookId, period] as const,
  currentValuation: (bookId: string) => [...portfolioKeys.all, "currentValuation", bookId] as const,
  recordCount: (bookId: string) => [...portfolioKeys.all, "recordCount", bookId] as const,
  twr: (bookId: string, period: string) => [...portfolioKeys.all, "twr", bookId, period] as const,
  dividends: (bookId: string, groupBy: string, year?: number) => [...portfolioKeys.all, "dividends", bookId, groupBy, year] as const,
  benchmark: (assetId: string, period: string) => [...portfolioKeys.all, "benchmark", assetId, period] as const,
};

export function useBooksQuery() {
  return useQuery({
    queryKey: portfolioKeys.books(),
    queryFn: getBooks,
  });
}

export function useValueChartQuery(bookId: string, period: string) {
  return useQuery({
    queryKey: portfolioKeys.valueChart(bookId, period),
    queryFn: async () => {
      const data = await getValueChart(bookId, period);
      if (data && data.value_points) {
        return {
          ...data,
          value_points: data.value_points.filter((p) => p.price > 0),
        };
      }
      return data;
    },
    enabled: !!bookId && !!period,
  });
}

export function usePerformanceQuery(bookId: string, period: string) {
  return useQuery({
    queryKey: portfolioKeys.performance(bookId, period),
    queryFn: () => getPerformanceResult(bookId, period),
    enabled: !!bookId && !!period,
  });
}

export function useCurrentValuationQuery(bookId: string) {
  return useQuery({
    queryKey: portfolioKeys.currentValuation(bookId),
    queryFn: () => getCurrentValuation(bookId),
    enabled: !!bookId,
  });
}

export function useRecordCountQuery(bookId: string) {
  return useQuery({
    queryKey: portfolioKeys.recordCount(bookId),
    queryFn: () => getRecordCount(bookId),
    enabled: !!bookId && bookId !== "global",
  });
}

export function useTWRChartQuery(bookId: string, period: string) {
  return useQuery({
    queryKey: portfolioKeys.twr(bookId, period),
    queryFn: () => getTWRChart(bookId, period),
    enabled: !!bookId && !!period,
  });
}

export function useDividendsQuery(bookId: string, groupBy: "month" | "year" = "month", year?: number) {
  return useQuery({
    queryKey: portfolioKeys.dividends(bookId, groupBy, year),
    queryFn: () => bookId === "global" 
      ? getGlobalDividendsReceived(groupBy, year)
      : getDividendsReceived(bookId, groupBy, year),
    enabled: !!bookId,
  });
}

export function useBenchmarkQuery(assetId: string | null, period: string) {
  return useQuery({
    queryKey: portfolioKeys.benchmark(assetId || "", period),
    queryFn: () => assetId ? getBenchmarkChart(assetId, period) : null,
    enabled: !!assetId && !!period,
  });
}
